import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { MessageSquare, Search, ArrowUp, ArrowDown, FolderTree, MoreVertical, Trash2, Filter, Archive, ArchiveRestore, Pencil, Link2, Lock, PinOff, Share2, FolderOpen, FileText, BookOpen, Lightbulb, Loader2, ArrowRight, RotateCcw, Globe, WifiOff, X, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { RENDER_WINDOW_STEP, growRenderWindow, retryDelayMs } from "@/lib/threadHistoryPure";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import { queryClient } from "@/lib/queryClient";
import { chatThreadsService, resolveThreadWorkspaceScope, type AccessibleThreadSummary } from "@/services/chatThreadsService";
import { ragService } from "@/services/ragService";
import { EmbeddedSearchMark } from "@/components/EmbeddedSearchMark";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { universalSearchService, DEFAULT_SEARCH_FILTERS } from "@/services/universalSearchService";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { PinVerificationDialog } from "@/components/PinVerificationDialog";
import { ThreadLinkingDialog } from "@/components/ThreadLinkingDialog";
import { ShareThreadDialog } from "@/components/ShareThreadDialog";
import { cn } from "@/lib/utils";
import { IconSelectMenu } from "@/components/CompactToolbar";
import type { MastermindSpace, Topic } from "@shared/schema";

interface GlobalSearchResult {
  type: "space" | "topic" | "thread" | "source" | "knowledge";
  id: string;
  title: string;
  timestamp?: string;
  spaceId?: string;
}

const globalSearchTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  space: FolderOpen,
  topic: Lightbulb,
  thread: MessageSquare,
  source: FileText,
  knowledge: BookOpen,
};
const globalSearchTypeLabels: Record<string, string> = {
  space: "Space",
  topic: "Topic",
  thread: "Conversation",
  source: "Source",
  knowledge: "Document",
};
const globalSearchTypeColors: Record<string, string> = {
  space: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  topic: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  thread: "bg-green-500/10 text-green-600 dark:text-green-400",
  source: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  knowledge: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

type ThreadSortField = "createdAt" | "updatedAt" | "title";

type SearchScope = "local" | "local-all" | "global";

type ThreadListScope = "all" | "current" | "personal";

const ACCESS_ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  owner_space: "Space owner",
  editor: "Editor",
  viewer: "Viewer",
  shared_invite: "Shared",
};

function accessRoleLabel(role?: string | null): string | null {
  if (!role || role === "owner") return null;
  return ACCESS_ROLE_LABELS[role] ?? role;
}

function workspaceLabel(
  workspaceId: string | null | undefined,
  workspaces: { id: string; name: string }[]
): string | null {
  if (!workspaceId) return "Personal";
  return workspaces.find((w) => w.id === workspaceId)?.name ?? "Workspace";
}

export default function Threads() {
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchScope, setSearchScope] = useState<SearchScope>("local");
  const [debouncedGlobalQuery, setDebouncedGlobalQuery] = useState("");
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { currentWorkspaceId, workspaces, isWorkspaceContext } = useWorkspace();
  const [threadListScope, setThreadListScope] = useState<ThreadListScope>("all");
  const [showTrash, setShowTrash] = useState(false);
  const [usedOwnedFallback, setUsedOwnedFallback] = useState(false);
  const fallbackToastShownRef = useRef(false);

  // Debounce search query for global search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedGlobalQuery(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close global search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setGlobalSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const {
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
  } = useViewPreferences<ThreadSortField>({
    storageKey: "threads",
    defaultViewMode: "list",
    defaultSortField: "createdAt",
    defaultSortDirection: "desc",
  });
  
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>("all");
  const [selectedTopicId, setSelectedTopicId] = useState<string>("all");
  // Thread state types - use partial thread type that works with ThreadSummary
  type ThreadRef = { id: string; title: string | null; isLocked?: boolean };
  type ThreadLinkRef = { id: string; title: string | null; linkedSpaceIds: string[] | null; topicId: string | null; tags: string[] | null };
  const [threadToDelete, setThreadToDelete] = useState<ThreadRef | null>(null);
  const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
  const [threadToLock, setThreadToLock] = useState<ThreadRef | null>(null);
  const [pendingThreadIdToOpen, setPendingThreadIdToOpen] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [threadToRename, setThreadToRename] = useState<ThreadRef | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [threadToLink, setThreadToLink] = useState<ThreadLinkRef | null>(null);
  const [threadToShare, setThreadToShare] = useState<ThreadRef | null>(null);
  const { toast } = useToast();

  const deleteThreadMutation = useMutation({
    mutationFn: async (threadId: string) => {
      await chatThreadsService.deleteChatThread(threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      queryClient.invalidateQueries({ queryKey: ["accessible-thread-summaries"] });
      toast({
        title: "Thread moved to trash",
        description: "It will be permanently removed after 30 days. You can restore it from Trash until then.",
      });
      setThreadToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete thread",
        variant: "destructive",
      });
    },
  });

  const restoreThreadMutation = useMutation({
    mutationFn: async (threadId: string) => {
      await chatThreadsService.restoreChatThread(threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accessible-thread-summaries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      toast({
        title: "Thread restored",
        description: "The conversation is active again.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Restore failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle thread lock mutation
  const toggleLockMutation = useMutation({
    mutationFn: async ({ threadId, lock }: { threadId: string; lock: boolean }) => {
      const result = await chatThreadsService.lockChatThread(threadId, lock, currentWorkspaceId ?? null);
      if (!result) {
        throw new Error("Failed to toggle lock");
      }
      return result;
    },
    onSuccess: (_, { lock }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/chat-thread-summaries", user?.id] });
      toast({
        title: lock ? "Thread Locked" : "Thread Unlocked",
        description: lock ? "This thread is now protected with your PIN" : "Thread is now accessible",
      });
      setThreadToLock(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Rename thread mutation
  const renameMutation = useMutation({
    mutationFn: async ({ threadId, title }: { threadId: string; title: string }) => {
      const result = await chatThreadsService.updateChatThread(threadId, { title }, currentWorkspaceId ?? null);
      if (!result) {
        throw new Error("Failed to rename thread");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      toast({
        title: "Thread renamed",
        description: "The thread title has been updated.",
      });
      setThreadToRename(null);
      setRenameValue("");
    },
    onError: (error: Error) => {
      toast({
        title: "Rename failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update linked spaces mutation
  const updateSpacesMutation = useMutation({
    mutationFn: async ({ threadId, spaceIds }: { threadId: string; spaceIds: string[] }) => {
      const result = await chatThreadsService.updateChatThread(threadId, { linkedSpaceIds: spaceIds }, currentWorkspaceId ?? null);
      if (!result) {
        throw new Error("Failed to update spaces");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
    },
  });

  // Update topic mutation
  const updateTopicMutation = useMutation({
    mutationFn: async ({ threadId, topicId }: { threadId: string; topicId: string | null }) => {
      const result = await chatThreadsService.updateChatThread(threadId, { topicId }, currentWorkspaceId ?? null);
      if (!result) {
        throw new Error("Failed to update topic");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
    },
  });

  // Update tags mutation
  const updateTagsMutation = useMutation({
    mutationFn: async ({ threadId, tags }: { threadId: string; tags: string[] }) => {
      const result = await chatThreadsService.updateChatThread(threadId, { tags }, currentWorkspaceId ?? null);
      if (!result) {
        throw new Error("Failed to update tags");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
    },
  });

  // Handler for lock button click
  // const handleLockClick = (thread: any) => {
  //   // Skip PIN validation for now - focus on topic display feature
  //   const shouldLock = !thread.isLocked;
  //   toggleLockMutation.mutate({ threadId: thread.id, lock: shouldLock });
  // };

  // Thread summary type for lightweight listing (no messages)
  type ThreadSummary = AccessibleThreadSummary;

  const rpcWorkspaceScope = useMemo(
    () =>
      showTrash
        ? "trash"
        : resolveThreadWorkspaceScope(
            threadListScope === "current" ? "current" : threadListScope,
            currentWorkspaceId
          ),
    [showTrash, threadListScope, currentWorkspaceId]
  );

  const searchWorkspaceScope = useMemo(() => {
    if (searchScope === "local-all" || searchScope === "global") return "all";
    return rpcWorkspaceScope === "trash" ? "all" : rpcWorkspaceScope;
  }, [searchScope, rpcWorkspaceScope]);

  const { data: pinStatus } = useQuery<{ hasPinSet: boolean; isSessionValid: boolean }>({
    queryKey: ["/api/pin/status"],
    queryFn: async () => {
      const { pinService } = await import("@/services/pinService");
      return pinService.getStatus();
    },
  });

  const isThreadLocked = (thread: ThreadSummary | ThreadRef) =>
    (thread as any)?.isLocked === "true" || (thread as any)?.isLocked === true;

  const {
    data: threads = [],
    isLoading,
    isFetching,
    isError,
    error: threadsError,
    refetch: refetchThreads,
  } = useQuery<ThreadSummary[]>({
    queryKey: ["accessible-thread-summaries", user?.id, rpcWorkspaceScope],
    queryFn: async () => {
      if (!user) return [];
      const startedAt = performance.now();
      const result = await chatThreadsService.getAccessibleThreadSummaries(rpcWorkspaceScope);
      if (import.meta.env.DEV) {
        // DS EDIT 6 — dev-only profiling (B0.1); never ships log noise to prod
        console.debug("[threads] list fetch", {
          ms: Math.round(performance.now() - startedAt),
          rows: result.threads.length,
          approxBytes: JSON.stringify(result.threads).length,
          scope: rpcWorkspaceScope,
        });
      }
      setUsedOwnedFallback(!!result.usedOwnedFallback);
      return result.threads;
    },
    enabled: !!user,
    // DS EDIT 6 — SWR tuning: fresh 15s, cached 5min, revalidate on
    // focus/reconnect, retry twice with exponential backoff.
    staleTime: 15_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 2,
    retryDelay: retryDelayMs,
  });

  const threadIdsForEmbed = useMemo(() => threads.map((thread) => thread.id), [threads]);
  const { data: threadEmbeddingStatus = {} } = useQuery<Record<string, boolean>>({
    queryKey: ["thread-embedding-status", threadIdsForEmbed.slice().sort().join(",")],
    queryFn: () => ragService.getThreadEmbeddingStatusForThreads(threadIdsForEmbed),
    enabled: !!user && threadIdsForEmbed.length > 0,
    staleTime: 60_000,
  });

  const handleThreadSearchAction = async (thread: ThreadSummary, action: "embed" | "remove") => {
    const title = thread.title || "Thread";
    try {
      if (action === "remove") {
        await ragService.deleteThreadEmbeddings(thread.id);
        toast({ title: "Removed from search", description: `"${title}" is no longer embedded.` });
      } else {
        await ragService.embedThread(thread.id);
        toast({
          title: threadEmbeddingStatus[thread.id] ? "Re-embedded for search" : "Embedded for search",
          description: threadEmbeddingStatus[thread.id]
            ? `"${title}" is updated for semantic search.`
            : `"${title}" is now searchable by meaning.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["thread-embedding-status"] });
    } catch (err) {
      toast({
        title: action === "remove" ? "Remove failed" : "Embed failed",
        description: err instanceof Error ? err.message : "Could not update thread embeddings.",
        variant: "destructive",
      });
    }
  };

  // DS EDIT 6 — offline awareness + windowed rendering. Only RENDER_WINDOW_STEP
  // rows mount at a time; an IntersectionObserver sentinel grows the window as
  // the user scrolls, so 1,000-thread histories never flood the DOM.
  const isOnline = useNetworkStatus();
  const [renderWindow, setRenderWindow] = useState(RENDER_WINDOW_STEP);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fallbackToastShownRef.current = false;
  }, [rpcWorkspaceScope, user?.id]);

  useEffect(() => {
    if (!usedOwnedFallback || fallbackToastShownRef.current) return;
    fallbackToastShownRef.current = true;
    toast({
      title: "Showing your threads only",
      description:
        "Shared threads could not be loaded in time. Your owned conversations are still listed.",
    });
  }, [usedOwnedFallback, toast]);

  const { data: allScopeSearchThreads = [] } = useQuery<ThreadSummary[]>({
    queryKey: ["accessible-thread-summaries", user?.id, "all", "search-helper"],
    queryFn: async () => {
      if (!user) return [];
      const result = await chatThreadsService.getAccessibleThreadSummaries("all");
      return result.threads;
    },
    enabled: !!user && searchScope === "local-all" && searchQuery.trim().length > 0,
    staleTime: 30000,
  });

  const { data: spaces = [] } = useQuery<MastermindSpace[]>({
    queryKey: ['/api/spaces', user?.id, currentWorkspaceId ?? 'personal'],
    queryFn: async () => {
      if (!user) return [];
      return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
    },
    enabled: !!user,
  });

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ['/api/topics', currentWorkspaceId ?? null],
    queryFn: async () => {
      const { topicsService } = await import("@/services/topicsService");
      return topicsService.getAllTopics(currentWorkspaceId ?? null);
    },
  });

  const getSpaceNames = (linkedSpaceIds: string[] | null) => {
    if (!linkedSpaceIds || linkedSpaceIds.length === 0) return [];
    return linkedSpaceIds
      .map(id => spaces.find(s => s.id === id)?.name)
      .filter(Boolean) as string[];
  };

  // Create a topic name map for efficient lookup and ensure it's updated when topics change
  const topicNameMap = useMemo(() => {
    if (!Array.isArray(topics)) return {};
    return topics.reduce((map, topic) => {
      map[topic.id] = topic.name;
      return map;
    }, {} as Record<string, string>);
  }, [topics]);

  // Global search (only when scope is global and query long enough)
  const showGlobalDropdown = searchScope === "global" && (globalSearchOpen || searchQuery.length >= 2) && debouncedGlobalQuery.length >= 2;
  const { data: globalSearchData, isLoading: globalSearchLoading } = useQuery({
    queryKey: ["threads-page-global-search", debouncedGlobalQuery, searchWorkspaceScope],
    queryFn: async () => {
      const res = await universalSearchService.search(debouncedGlobalQuery, DEFAULT_SEARCH_FILTERS, undefined, 20);
      // Adapt universal search results (thread | document) to this page's dropdown shape
      const results: GlobalSearchResult[] = res.results.map((r) => ({
        type: r.resultType === "document" ? "knowledge" : "thread",
        id: r.id,
        title: r.title,
        timestamp: r.updatedAt ?? undefined,
        spaceId: r.spaceId ?? undefined,
      }));
      return { results };
    },
    enabled: searchScope === "global" && debouncedGlobalQuery.length >= 2,
    staleTime: 30000,
  });
  const globalResults = Array.isArray(globalSearchData?.results) ? globalSearchData.results : [];
  const groupedGlobalResults = useMemo(() => {
    return globalResults.reduce((acc: Record<string, GlobalSearchResult[]>, r) => {
      if (!acc[r.type]) acc[r.type] = [];
      acc[r.type].push(r);
      return acc;
    }, {});
  }, [globalResults]);

  const handleSelectGlobalResult = useCallback(
    (result: GlobalSearchResult) => {
      setSearchQuery("");
      setGlobalSearchOpen(false);
      switch (result.type) {
        case "space":
          setLocation(`/spaces/${result.id}`);
          break;
        case "topic":
          setLocation(result.spaceId ? `/spaces/${result.spaceId}` : "/topics");
          break;
        case "thread":
          setLocation(`/chat?threadId=${result.id}`);
          break;
        case "source":
          setLocation(`/source/${result.id}`);
          break;
        case "knowledge":
          setLocation(`/document/${result.id}`);
          break;
        default:
          setLocation("/");
      }
    },
    [setLocation]
  );

  const sourceThreadsForLocalSearch =
    searchScope === "local-all" ? allScopeSearchThreads : threads;

  const filteredThreads = sourceThreadsForLocalSearch
    .filter(thread => {
      if (showTrash) return true;
      const isArchived =
        (thread as any).status === "archived" ||
        (thread as any).isArchived === "true" ||
        (thread as any).isArchived === true;
      const matchesArchiveFilter = showArchived ? isArchived : !isArchived;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        searchScope === "global" || q === ""
          ? true
          : (thread.title?.toLowerCase().includes(q) ||
            thread.topic?.toLowerCase().includes(q));
      
      const matchesSpace = selectedSpaceId === "all" || 
        (selectedSpaceId === "none" 
          ? !thread.linkedSpaceIds || thread.linkedSpaceIds.length === 0 
          : thread.linkedSpaceIds?.includes(selectedSpaceId));
      
      const matchesTopic = selectedTopicId === "all" || 
        (selectedTopicId === "none" ? !thread.topicId : thread.topicId === selectedTopicId);
      
      return matchesArchiveFilter && matchesSearch && matchesSpace && matchesTopic;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      if (sortField === "title") {
        comparison = (a.title || "").localeCompare(b.title || "");
      } else if (sortField === "createdAt") {
        comparison =
          new Date(a.createdAt ?? a.updatedAt ?? 0).getTime() -
          new Date(b.createdAt ?? b.updatedAt ?? 0).getTime();
      } else if (sortField === "updatedAt") {
        comparison = new Date(a.updatedAt || a.createdAt || 0).getTime() - new Date(b.updatedAt || b.createdAt || 0).getTime();
      }
      
      return sortDirection === "asc" ? comparison : -comparison;
    });

  const threadCounts = useMemo(() => {
    const owned = threads.filter((t) => !t.accessRole || t.accessRole === "owner").length;
    const shared = threads.length - owned;
    const active = threads.filter((t) => {
      const isArchived =
        (t as any).status === "archived" ||
        (t as any).isArchived === "true" ||
        (t as any).isArchived === true;
      return !isArchived;
    }).length;
    const archived = threads.length - active;
    return { owned, shared, active, archived, total: threads.length };
  }, [threads]);

  // DS EDIT 6 — reset the render window whenever the visible set changes shape
  useEffect(() => {
    setRenderWindow(RENDER_WINDOW_STEP);
  }, [searchQuery, showTrash, showArchived, selectedSpaceId, selectedTopicId, searchScope, rpcWorkspaceScope]);

  // DS EDIT 6 — sentinel observer; disconnects automatically once everything is
  // rendered (effect re-runs and bails), so no infinite observe loop.
  const totalFilteredCount = filteredThreads.length;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || renderWindow >= totalFilteredCount) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setRenderWindow((current) => growRenderWindow(current, totalFilteredCount));
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [renderWindow, totalFilteredCount]);

  const sortOptions = [
    { value: "createdAt", label: "Date Created" },
    { value: "updatedAt", label: "Last Updated" },
    { value: "title", label: "Title" },
  ] as const;

  if (isLoading) {
    // DS EDIT 6 — skeleton rows instead of a bare "loading" line: 60/40 split
    // simulating title + date, so the page feels alive, never broken.
    return (
      <div className="p-6 space-y-3" data-testid="threads-skeleton">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2">
            <Skeleton className="h-9 w-9 rounded-lg shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/5 rounded-lg" />
              <Skeleton className="h-3 w-2/5 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError && threads.length === 0) {
    // DS EDIT 6 — amber (never red), gentle copy, always a clear exit path.
    return (
      <div className="p-6" data-testid="threads-error-state">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-6 py-10 text-center animate-in slide-in-from-top-2 duration-300">
          <p className="font-medium text-amber-800 dark:text-amber-200">
            We couldn't load your conversations.
          </p>
          <p className="text-sm text-muted-foreground max-w-md">
            {!isOnline
              ? "You appear to be offline. Check your connection?"
              : threadsError instanceof Error
                ? threadsError.message
                : "Something went wrong on our end. We're on it."}
          </p>
          <Button variant="outline" onClick={() => refetchThreads()} data-testid="button-threads-try-again">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b bg-background relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Chat History</h1>
            <p className="text-muted-foreground">Browse and search all your AI conversations</p>
          </div>
        </div>

        {/* DS EDIT 5aug26 — icon-only filter controls, all on one row */}
        <div className="flex flex-wrap items-center gap-1.5">
          {!(searchOpen || searchQuery) ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => setSearchOpen(true)}
              aria-label="Search conversations"
              data-testid="button-search-threads-toggle"
            >
              <Search className="h-4 w-4" />
            </Button>
          ) : (
          <div ref={searchContainerRef} className="relative flex-1 min-w-40">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search conversations..."
              className="pl-10 pr-9 h-9"
              autoFocus
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (searchScope === "global") setGlobalSearchOpen(true);
              }}
              onFocus={() => searchScope === "global" && searchQuery.trim().length >= 2 && setGlobalSearchOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setSearchQuery("");
                  setSearchOpen(false);
                  setGlobalSearchOpen(false);
                }
              }}
              onBlur={() => {
                if (!searchQuery) setSearchOpen(false);
              }}
              data-testid="input-search-threads"
            />
            {searchScope === "global" && globalSearchLoading && debouncedGlobalQuery.length >= 2 ? (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground animate-spin" />
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0.5 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => {
                  setSearchQuery("");
                  setSearchOpen(false);
                  setGlobalSearchOpen(false);
                }}
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            {showGlobalDropdown && (
              <div
                className="absolute top-full left-0 right-0 z-50 mt-1 rounded-lg border bg-popover text-popover-foreground shadow-lg overflow-hidden"
                data-testid="threads-global-search-dropdown"
              >
                <div className="max-h-[min(70vh,400px)] overflow-y-auto p-1">
                  {globalResults.length === 0 && !globalSearchLoading && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No results for &quot;{debouncedGlobalQuery}&quot;
                    </div>
                  )}
                  {Object.entries(groupedGlobalResults).map(([type, results]) => (
                    <div key={type} className="mb-2">
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {globalSearchTypeLabels[type] ?? type}s
                      </div>
                      {results.map((result) => {
                        const Icon = globalSearchTypeIcons[result.type];
                        return (
                          <button
                            key={`${result.type}-${result.id}`}
                            type="button"
                            onClick={() => handleSelectGlobalResult(result)}
                            className={cn(
                              "w-full flex items-center gap-3 rounded-md px-2 py-2 text-left",
                              "hover:bg-accent hover:text-accent-foreground transition-colors"
                            )}
                            data-testid={`threads-global-result-${result.type}-${result.id}`}
                          >
                            <div
                              className={cn(
                                "p-1.5 rounded shrink-0",
                                globalSearchTypeColors[result.type] ?? "bg-muted text-muted-foreground"
                              )}
                            >
                              {Icon ? <Icon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium truncate">{result.title}</span>
                                <span className="text-xs text-muted-foreground shrink-0">
                                  {globalSearchTypeLabels[result.type]}
                                </span>
                              </div>
                              {result.timestamp && (
                                <p className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(result.timestamp), { addSuffix: true })}
                                </p>
                              )}
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          <IconSelectMenu
            icon={MessageSquare}
            label="Search in"
            value={searchScope}
            onValueChange={(v) => setSearchScope(v as SearchScope)}
            testId="select-search-scope"
            active={searchScope !== "local"}
            options={[
              { value: "local", label: "In threads" },
              { value: "local-all", label: "All workspaces" },
              { value: "global", label: "Everywhere" },
            ]}
          />

          <IconSelectMenu
            icon={Globe}
            label="Thread scope"
            value={showTrash ? "trash" : threadListScope}
            onValueChange={(v) => {
              if (v === "trash") {
                setShowTrash(true);
                setShowArchived(false);
              } else {
                setShowTrash(false);
                setThreadListScope(v as ThreadListScope);
              }
            }}
            testId="select-thread-scope"
            active={showTrash || threadListScope !== "all"}
            options={[
              { value: "all", label: "All threads" },
              { value: "current", label: isWorkspaceContext ? "Current workspace" : "Personal" },
              { value: "personal", label: "Personal only" },
              { value: "trash", label: "Trash" },
            ]}
          />

          <IconSelectMenu
            icon={Filter}
            label="Space filter"
            value={selectedSpaceId}
            onValueChange={setSelectedSpaceId}
            testId="select-filter-space"
            active={selectedSpaceId !== "all"}
            options={[
              { value: "all", label: "All Spaces" },
              { value: "none", label: "No Space" },
              ...spaces.map((space) => ({ value: space.id, label: space.name })),
            ]}
          />

          <IconSelectMenu
            icon={FolderTree}
            label="Topic filter"
            value={selectedTopicId}
            onValueChange={setSelectedTopicId}
            testId="select-filter-topic"
            active={selectedTopicId !== "all"}
            options={[
              { value: "all", label: "All Topics" },
              { value: "none", label: "No Topic" },
              ...(Array.isArray(topics) ? topics : []).map((topic) => ({ value: topic.id, label: topic.name })),
            ]}
          />

          <IconSelectMenu
            label="Sort by"
            value={sortField}
            onValueChange={(v) => setSortField(v as ThreadSortField)}
            testId="select-sort-field"
            options={sortOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
          />

          <Button
            variant="outline"
            size="icon"
            onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
            data-testid="button-sort-direction"
          >
            {sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
          </Button>

          <Button
            variant={showTrash ? "secondary" : "outline"}
            size="icon"
            onClick={() => {
              setShowTrash(!showTrash);
              if (!showTrash) setShowArchived(false);
            }}
            data-testid="button-toggle-trash"
            title={showTrash ? "Show active threads" : "Show trash"}
          >
            <Trash2 className="h-4 w-4" />
          </Button>

          <Button
            variant={showArchived ? "secondary" : "outline"}
            size="icon"
            onClick={() => setShowArchived(!showArchived)}
            disabled={showTrash}
            data-testid="button-toggle-archived"
            title={showArchived ? "Show Active" : "Show Archived"}
          >
            {showArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </Button>
          
          {/* DS EDIT 5aug26 — "Showing X of Y" line removed (operator markup); compact counts only */}
          {!showTrash && (
            <span className="text-xs text-muted-foreground min-w-0" data-testid="threads-count-summary">
              {threadCounts.owned} owned · {threadCounts.shared} shared
              {!showArchived && ` · ${threadCounts.active} active · ${threadCounts.archived} archived`}
            </span>
          )}
        </div>
      </div>

      {/* DS EDIT 6 — offline banner (amber, dismisses itself on reconnect) */}
      {!isOnline && (
        <div
          className="flex items-center justify-center gap-2 border-b border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-800 dark:text-amber-200 animate-in slide-in-from-top-2 duration-300"
          data-testid="banner-threads-offline"
        >
          <WifiOff className="h-4 w-4" />
          You're offline — showing your cached conversations. They'll refresh when you reconnect.
        </div>
      )}

      {/* DS EDIT 6 — stale-revalidation indicator: thin teal shimmer, list stays interactive */}
      {isFetching && !isLoading && (
        <div className="h-0.5 w-full overflow-hidden bg-transparent" data-testid="bar-threads-revalidating">
          <div className="h-full w-1/3 animate-pulse rounded bg-teal-600/70" />
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-1">
          {filteredThreads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg">
              {showTrash ? (
                <Trash2 className="h-12 w-12 text-muted-foreground mb-4" />
              ) : showArchived ? (
                <Archive className="h-12 w-12 text-muted-foreground mb-4" />
              ) : (
                <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              )}
              <h3 className="text-lg font-medium mb-2">
                {showTrash
                  ? "Trash is empty"
                  : showArchived
                    ? "No archived conversations"
                    : searchQuery || selectedSpaceId !== "all" || selectedTopicId !== "all"
                      ? "No conversations match your search"
                      : "Your conversations will appear here"}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {searchQuery || selectedSpaceId !== "all" || selectedTopicId !== "all"
                  ? "Try adjusting your search or filters"
                  : showTrash
                    ? "Deleted threads appear here for 30 days before permanent removal"
                    : showArchived
                      ? "Archived conversations will appear here after using Archive thread in chat"
                      : "Ready to start one?"}
              </p>
              {(searchQuery || selectedSpaceId !== "all" || selectedTopicId !== "all") && !showTrash && !showArchived && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedSpaceId("all");
                    setSelectedTopicId("all");
                  }}
                  data-testid="button-clear-thread-filters"
                >
                  Clear search
                </Button>
              )}
              {!searchQuery && selectedSpaceId === "all" && selectedTopicId === "all" && !showArchived && !showTrash && (
                <Button className="mt-4" onClick={() => setLocation("/")}>
                  Start New Chat
                </Button>
              )}
            </div>
          ) : (
            filteredThreads.slice(0, renderWindow).map(thread => (
              <div 
                key={thread.id} 
                className="flex items-center gap-3 px-3 py-2 rounded-md hover-elevate cursor-pointer transition-all border-b last:border-b-0"
                onClick={() => {
                  if (isThreadLocked(thread) && !pinStatus?.isSessionValid) {
                    setPendingThreadIdToOpen(thread.id);
                  } else {
                    setLocation(`/chat?threadId=${thread.id}`);
                  }
                }}
                data-testid={`thread-card-${thread.id}`}
              >
                {/* Icon */}
                <MessageSquare className="h-4 w-4 text-primary shrink-0" />
                {isThreadLocked(thread) && (
                  <Lock className="h-3.5 w-3 text-red-500 shrink-0" />
                )}
                
                {/* Title and badges - takes most space */}
                {/* Title and topic display with improved visual hierarchy */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{thread.title || "Untitled"}</span>
                    {threadEmbeddingStatus[thread.id] && <EmbeddedSearchMark />}
                    {thread.topicId && topicNameMap[thread.topicId] && (
                      <>
                        <span className="text-muted-foreground/60 text-xs mx-1">•</span>
                        <span className="text-primary text-xs truncate">
                          {topicNameMap[thread.topicId]}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Show space info on second line */}
                  {getSpaceNames(thread.linkedSpaceIds).length > 0 && (
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                        {getSpaceNames(thread.linkedSpaceIds)[0]}
                      </Badge>
                      {threadListScope === "all" && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                          {workspaceLabel(thread.workspaceId, workspaces)}
                        </Badge>
                      )}
                      {accessRoleLabel(thread.accessRole) && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                          {accessRoleLabel(thread.accessRole)}
                        </Badge>
                      )}
                    </div>
                  )}
                  {threadListScope === "all" && getSpaceNames(thread.linkedSpaceIds).length === 0 && (
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                        {workspaceLabel(thread.workspaceId, workspaces)}
                      </Badge>
                      {accessRoleLabel(thread.accessRole) && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                          {accessRoleLabel(thread.accessRole)}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Date - compact (DS EDIT 5aug26: smaller, "about " prefix dropped) */}
                <span className="text-xs text-muted-foreground/80 shrink-0">
                  {(() => {
                    const raw = thread.updatedAt ?? thread.updated_at ?? thread.createdAt ?? thread.created_at;
                    const date = raw ? new Date(raw) : new Date();
                    return isNaN(date.getTime()) ? "—" : formatDistanceToNow(date, { addSuffix: true }).replace(/^about /, "");
                  })()}
                </span>
                
                {/* Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 shrink-0"
                      data-testid={`button-thread-menu-${thread.id}`}
                    >
                      <MoreVertical className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    {showTrash ? (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          restoreThreadMutation.mutate(thread.id);
                        }}
                        data-testid={`button-restore-thread-${thread.id}`}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" /> Restore
                      </DropdownMenuItem>
                    ) : (
                      <>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameValue(thread.title || "");
                        setThreadToRename(thread);
                      }}
                      data-testid={`button-rename-thread-${thread.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-2" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setThreadToLink(thread);
                      }}
                      data-testid={`button-link-thread-${thread.id}`}
                    >
                      <Link2 className="h-4 w-4 mr-2" /> Link to Space/Topic
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setThreadToLock(thread);
                        setIsPinDialogOpen(true);
                      }}
                      data-testid={`button-lock-thread-${thread.id}`}
                    >
                      {isThreadLocked(thread) ? (
                        <><PinOff className="h-4 w-4 mr-2" /> Unlock Thread</>
                      ) : (
                        <><Lock className="h-4 w-4 mr-2" /> Lock Thread</>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setThreadToShare(thread);
                      }}
                      data-testid={`button-share-thread-${thread.id}`}
                    >
                      <Share2 className="h-4 w-4 mr-2" /> Share Thread
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {threadEmbeddingStatus[thread.id] ? (
                      <>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleThreadSearchAction(thread, "embed");
                          }}
                        >
                          <Sparkles className="h-4 w-4 mr-2" /> Re-embed for search
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleThreadSearchAction(thread, "remove");
                          }}
                        >
                          <X className="h-4 w-4 mr-2" /> Remove from search
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleThreadSearchAction(thread, "embed");
                        }}
                      >
                        <Sparkles className="h-4 w-4 mr-2" /> Embed for search
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setThreadToDelete(thread);
                      }}
                      className="text-destructive focus:text-destructive"
                      data-testid={`button-delete-thread-${thread.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))
          )}
          {/* DS EDIT 6 — infinite-scroll sentinel + subtle loading-more shimmer */}
          {renderWindow < filteredThreads.length && (
            <div ref={sentinelRef} className="py-2" data-testid="threads-scroll-sentinel">
              <div className="h-1 w-full overflow-hidden rounded bg-muted">
                <div className="h-full w-1/3 animate-pulse rounded bg-teal-600/70" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      <AlertDialog open={!!threadToDelete} onOpenChange={(open) => !open && setThreadToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Chat Thread</AlertDialogTitle>
            <AlertDialogDescription>
              Move "{threadToDelete?.title || "Untitled Conversation"}" to trash? It will be kept for 30 days and can be restored from Trash before it is permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-thread">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => threadToDelete && deleteThreadMutation.mutate(threadToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-thread"
            >
              {deleteThreadMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PIN Verification Dialog for thread locking */}
      <PinVerificationDialog
        open={isPinDialogOpen}
        onOpenChange={setIsPinDialogOpen}
        onSuccess={() => {
          if (threadToLock) {
            const shouldLock = !isThreadLocked(threadToLock);
            toggleLockMutation.mutate({ threadId: threadToLock.id, lock: shouldLock });
          }
        }}
        title={threadToLock && isThreadLocked(threadToLock) ? "Unlock Thread" : "Lock Thread"}
        description="Enter your PIN to continue"
      />

      {/* PIN Verification Dialog for opening a locked thread */}
      <PinVerificationDialog
        open={!!pendingThreadIdToOpen}
        onOpenChange={(open) => !open && setPendingThreadIdToOpen(null)}
        onSuccess={() => {
          if (pendingThreadIdToOpen) {
            queryClient.invalidateQueries({ queryKey: ["/api/pin/status"] });
            setLocation(`/chat?threadId=${pendingThreadIdToOpen}`);
            setPendingThreadIdToOpen(null);
          }
        }}
        title="Unlock Thread"
        description="Enter your PIN to open this locked thread"
      />

      <ShareThreadDialog
        open={!!threadToShare}
        onOpenChange={(open) => !open && setThreadToShare(null)}
        threadId={threadToShare?.id ?? ""}
        threadName={threadToShare?.title || "Untitled Chat"}
      />

      {/* Rename Dialog */}
      <Dialog open={!!threadToRename} onOpenChange={(open) => !open && setThreadToRename(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Thread</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename-input">Thread Name</Label>
            <Input
              id="rename-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Enter thread name..."
              className="mt-2"
              onKeyDown={(e) => {
                if (e.key === "Enter" && renameValue.trim() && threadToRename) {
                  renameMutation.mutate({ threadId: threadToRename.id, title: renameValue.trim() });
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setThreadToRename(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (threadToRename && renameValue.trim()) {
                  renameMutation.mutate({ threadId: threadToRename.id, title: renameValue.trim() });
                }
              }}
              disabled={!renameValue.trim() || renameMutation.isPending}
            >
              {renameMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Linking Dialog */}
      {threadToLink && (
        <ThreadLinkingDialog
          open={!!threadToLink}
          onOpenChange={(open) => !open && setThreadToLink(null)}
          linkedSpaceIds={threadToLink.linkedSpaceIds || []}
          topicId={threadToLink.topicId || null}
          tags={threadToLink.tags || []}
          onUpdateSpaces={(spaceIds) => {
            updateSpacesMutation.mutate({ threadId: threadToLink.id, spaceIds });
          }}
          onUpdateTopic={(topicId) => {
            updateTopicMutation.mutate({ threadId: threadToLink.id, topicId });
          }}
          onUpdateTags={(tags) => {
            updateTagsMutation.mutate({ threadId: threadToLink.id, tags });
          }}
        />
      )}
    </div>
  );
}
